import { useCallback } from "react";

import { ENTRY_DATA_SECTION_KEYS } from "@frontend/common";

import { DealLaScSection } from "@/types/dealLaSc.types";
import {
  DEFAULT_FOCUS_STATUS,
  useTableCreatorFocusStatus,
} from "@FillInTableContainer/shared/context/TableCreatorFocusStatusContext";
import { useTableCreatorCurrentHistory } from "@FillInTableContainer/shared/context/TableCreatorHistoryContext";
import {
  useTableCreatorPositionInfo,
  DEFAULT_POSITION_INFO,
} from "@FillInTableContainer/shared/context/TableCreatorPositionInfoContext";
import {
  useValidatedInTableCreator,
  DEFAULT_VALIDATED_INFO,
} from "@FillInTableContainer/shared/context/ValidatedInTableCreatorContext";
import { useCheckDisabledDelete } from "@FillInTableContainer/shared/hooks/useCheckDisabledDelete";
import { useEntryDataState } from "@stores/entryData";
import { getPressIdFromURL } from "@utils/getStateFromURL";

import type { ArticleEntryData } from "@/types/articleEntryData.types";

interface UseHandleTableCreatorTableReturn {
  handleClickFinancialTermsTableDeleteButton: () => void;
  handleKeyDownToDeleteFinancialTermsTable: (event: KeyboardEvent) => void;
}

export interface TableInfoProps {
  groupTableKey: DealLaScSection;
  tableKey: string | null;
  groupTableIndex: number;
  tableIndex: number;
  tableCount: number;
}

export const useHandleDealLaScFinancialTermsTable = (
  tablePropsInfo: TableInfoProps,
): UseHandleTableCreatorTableReturn => {
  const { groupTableKey, tableKey, groupTableIndex, tableIndex } =
    tablePropsInfo;

  const { changeCurrentHistory } = useTableCreatorCurrentHistory();
  const articleSection = ENTRY_DATA_SECTION_KEYS.dealsLaSc;

  const { info, changeInfo } = useTableCreatorPositionInfo();
  const [entryData, setEntryData] = useEntryDataState(getPressIdFromURL());
  const { focusStatus, changeFocusStatus } = useTableCreatorFocusStatus();
  const { changeValidatedInfo } = useValidatedInTableCreator();
  const { checkDisabledDeleteTable } = useCheckDisabledDelete();

  const financialTermsTableLength =
    entryData[articleSection]?.["Main Financial Terms Table"]?.length || 0;

  const handleClearTable = useCallback(() => {
    const targetFinancialTermsData =
      entryData[articleSection]?.["Main Financial Terms Table"];
    const clearFinancialTermsTable = (): ArticleEntryData => {
      return {
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.dealsLaSc]: {
          ...entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc],
          "Main Financial Terms Table": targetFinancialTermsData?.map(
            (tableInfo, tableInfoIndex) => {
              if (tableInfoIndex === info.tableIndex) {
                return {
                  ...tableInfo,
                  "Disclosed Payment Sum(LA&SC)": {
                    ...tableInfo?.["Disclosed Payment Sum(LA&SC)"],
                    value: [],
                  },
                  "Milestone Payment And Detail": tableInfo?.[
                    "Milestone Payment And Detail"
                  ]?.map((groupShellInfo) => {
                    return {
                      ...groupShellInfo,
                      "Milestone Payment Detail(LA&SC)": {
                        ...groupShellInfo?.["Milestone Payment Detail(LA&SC)"],
                        value: [],
                      },
                      "Milestone Payment(LA&SC)": {
                        ...groupShellInfo?.["Milestone Payment(LA&SC)"],
                        value: [],
                      },
                    };
                  }),
                  "Other Payment And Detail": tableInfo?.[
                    "Other Payment And Detail"
                  ]?.map((groupShellInfo) => {
                    return {
                      ...groupShellInfo,
                      "Other Payment Detail(LA&SC)": {
                        ...groupShellInfo?.["Other Payment Detail(LA&SC)"],
                        value: [],
                      },
                      "Other Payment(LA&SC)": {
                        ...groupShellInfo?.["Other Payment(LA&SC)"],
                        value: [],
                      },
                    };
                  }),
                  "Paying Partner Axis(LA&SC)": {
                    ...tableInfo?.["Paying Partner Axis(LA&SC)"],
                    value: [],
                  },
                  "Receiving Partner Axis(LA&SC)": {
                    ...tableInfo?.["Receiving Partner Axis(LA&SC)"],
                    value: [],
                  },
                  "Royalty(LA&SC)": {
                    ...tableInfo?.["Royalty(LA&SC)"],
                    value: [],
                  },
                  "Total Payment(LA&SC)": {
                    ...tableInfo?.["Total Payment(LA&SC)"],
                    value: [],
                  },
                  "Upfront Payment(LA&SC)": {
                    ...tableInfo?.["Upfront Payment(LA&SC)"],
                    value: [],
                  },
                };
              }
              return tableInfo;
            },
          ),
        },
      };
    };

    const clearPartnerAAxisTable = (): ArticleEntryData => {
      return {
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.dealsLaSc]: {
          ...entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc],
          "Partner Axis": {
            ...entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc]?.["Partner Axis"],
            "Partner A": (
              entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc]?.["Partner Axis"]?.[
                "Partner A"
              ] ?? []
            ).map((partnerAInfo) => ({
              ...partnerAInfo,
              "Partner A Name": {
                ...partnerAInfo["Partner A Name"],
                value: [],
              },
              "Partner A Relationship Context": {
                ...partnerAInfo["Partner A Relationship Context"],
                value: [],
              },
            })),
            "Partner A Responsibility & Rights": {
              ...(entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc]?.[
                "Partner Axis"
              ]?.["Partner A Responsibility & Rights"] ?? {
                options: [],
                value: [],
              }),
              value: [],
            },
          },
        },
      } as ArticleEntryData;
    };

    const clearPartnerBAxisTable = (): ArticleEntryData => {
      return {
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.dealsLaSc]: {
          ...entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc],
          "Partner Axis": {
            ...entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc]?.["Partner Axis"],
            "Partner B": (
              entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc]?.["Partner Axis"]?.[
                "Partner B"
              ] ?? []
            ).map((partnerBInfo) => ({
              ...partnerBInfo,
              "Partner B Name": {
                ...partnerBInfo["Partner B Name"],
                value: [],
              },
              "Partner B Relationship Context": {
                ...partnerBInfo["Partner B Relationship Context"],
                value: [],
              },
            })),
            "Partner B Responsibility & Rights": {
              ...(entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc]?.[
                "Partner Axis"
              ]?.["Partner B Responsibility & Rights"] ?? {
                options: [],
                value: [],
              }),
              value: [],
            },
          },
        },
      } as ArticleEntryData;
    };

    const clearPartnerCAxisTable = (): ArticleEntryData => {
      return {
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.dealsLaSc]: {
          ...entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc],
          "Partner Axis": {
            ...entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc]?.["Partner Axis"],
            "Partner C": (
              entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc]?.["Partner Axis"]?.[
                "Partner C"
              ] ?? []
            ).map((partnerCInfo) => ({
              ...partnerCInfo,
              "Partner C Name": {
                ...partnerCInfo["Partner C Name"],
                value: [],
              },
              "Partner C Relationship Context": {
                ...partnerCInfo["Partner C Relationship Context"],
                value: [],
              },
            })),
            "Partner C Responsibility & Rights": {
              ...(entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc]?.[
                "Partner Axis"
              ]?.["Partner C Responsibility & Rights"] ?? {
                options: [],
                value: [],
              }),
              value: [],
            },
          },
        },
      } as ArticleEntryData;
    };

    if (info.tableKey === "Main Financial Terms Table") {
      setEntryData(clearFinancialTermsTable());
      changeCurrentHistory({
        entryData: clearFinancialTermsTable(),
        changedDataInfo: {
          target: "table",
          action: "clear",
          position: {
            groupTableKey,
            groupTableIndex,
            tableKey,
            tableIndex,
            groupShellKey: null,
            groupShellIndex: null,
            subGroupShellIndex: null,
            subGroupShellKey: null,
            shellKey: null,
            shellIndex: null,
            valueIndex: null,
            value: null,
          },
        },
      });

      if (financialTermsTableLength > 1 && !checkDisabledDeleteTable()) {
        changeFocusStatus({ type: "delete", container: "table" });
      } else {
        changeFocusStatus(DEFAULT_FOCUS_STATUS);
      }
    }

    if (info.tableKey === "Partner A Axis") {
      setEntryData(clearPartnerAAxisTable());
      changeCurrentHistory({
        entryData: clearPartnerAAxisTable(),
        changedDataInfo: {
          target: "table",
          action: "clear",
          position: {
            groupTableKey,
            groupTableIndex,
            tableKey,
            tableIndex,
            groupShellKey: null,
            groupShellIndex: null,
            subGroupShellIndex: null,
            subGroupShellKey: null,
            shellKey: null,
            shellIndex: null,
            valueIndex: null,
            value: null,
          },
        },
      });
      changeFocusStatus(DEFAULT_FOCUS_STATUS);
    }

    if (info.tableKey === "Partner B Axis") {
      setEntryData(clearPartnerBAxisTable());
      changeCurrentHistory({
        entryData: clearPartnerBAxisTable(),
        changedDataInfo: {
          target: "table",
          action: "clear",
          position: {
            groupTableKey,
            groupTableIndex,
            tableKey,
            tableIndex,
            groupShellKey: null,
            groupShellIndex: null,
            subGroupShellIndex: null,
            subGroupShellKey: null,
            shellKey: null,
            shellIndex: null,
            valueIndex: null,
            value: null,
          },
        },
      });
      changeFocusStatus(DEFAULT_FOCUS_STATUS);
    }

    if (info.tableKey === "Partner C Axis") {
      setEntryData(clearPartnerCAxisTable());
      changeCurrentHistory({
        entryData: clearPartnerCAxisTable(),
        changedDataInfo: {
          target: "table",
          action: "clear",
          position: {
            groupTableKey,
            groupTableIndex,
            tableKey,
            tableIndex,
            groupShellKey: null,
            groupShellIndex: null,
            subGroupShellIndex: null,
            subGroupShellKey: null,
            shellKey: null,
            shellIndex: null,
            valueIndex: null,
            value: null,
          },
        },
      });
      changeFocusStatus({ type: "delete", container: "table" });
    }
  }, [
    articleSection,
    changeCurrentHistory,
    changeFocusStatus,
    checkDisabledDeleteTable,
    entryData,
    groupTableIndex,
    groupTableKey,
    info.tableIndex,
    info.tableKey,
    financialTermsTableLength,
    setEntryData,
    tableIndex,
    tableKey,
  ]);

  const handleDeleteTable = useCallback(() => {
    if (checkDisabledDeleteTable()) {
      return;
    }

    if (financialTermsTableLength > 1) {
      changeFocusStatus(DEFAULT_FOCUS_STATUS);

      const deleteFinancialTermsTable = (): ArticleEntryData => {
        return {
          ...entryData,
          [ENTRY_DATA_SECTION_KEYS.dealsLaSc]: {
            ...entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc],
            "Main Financial Terms Table": entryData[
              ENTRY_DATA_SECTION_KEYS.dealsLaSc
            ]?.["Main Financial Terms Table"]?.filter(
              (_, tableInfoIndex) => tableInfoIndex !== info.tableIndex,
            ),
          },
        };
      };

      if (info.groupTableKey === "Main Financial Terms Table") {
        setEntryData(deleteFinancialTermsTable());
        changeCurrentHistory({
          entryData: deleteFinancialTermsTable(),
          changedDataInfo: {
            target: "table",
            action: "delete",
            position: {
              groupTableKey,
              groupTableIndex,
              tableKey,
              tableIndex,
              groupShellKey: null,
              groupShellIndex: null,
              subGroupShellIndex: null,
              subGroupShellKey: null,
              shellKey: null,
              shellIndex: null,
              valueIndex: null,
              value: null,
            },
          },
        });
        changeInfo(DEFAULT_POSITION_INFO);
      }
    }
  }, [
    checkDisabledDeleteTable,
    financialTermsTableLength,
    changeFocusStatus,
    info.groupTableKey,
    info.tableIndex,
    entryData,
    setEntryData,
    changeCurrentHistory,
    groupTableKey,
    groupTableIndex,
    tableKey,
    tableIndex,
    changeInfo,
  ]);

  const handleClickFinancialTermsTableDeleteButton = () => {
    if (focusStatus.type === "clear") handleClearTable();
    if (focusStatus.type === "delete") handleDeleteTable();
  };

  const handleKeyDownToDeleteFinancialTermsTable = useCallback(
    (event: KeyboardEvent) => {
      if (focusStatus.type === "clear" && event.key === "Delete") {
        handleClearTable();
      }
      if (focusStatus.type === "delete" && event.key === "Delete") {
        handleDeleteTable();
      }
      changeValidatedInfo(DEFAULT_VALIDATED_INFO);
    },
    [
      focusStatus.type,
      changeValidatedInfo,
      handleClearTable,
      handleDeleteTable,
    ],
  );

  return {
    handleClickFinancialTermsTableDeleteButton,
    handleKeyDownToDeleteFinancialTermsTable,
  };
};
